import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import axios from 'axios';
import { FaLock, FaEnvelope } from 'react-icons/fa';

// Validation schemas
const signUpSchema = yup.object().shape({
  email: yup.string().email('Invalid email format').required('Email is required'),
  password: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

const signInSchema = yup.object().shape({
  email: yup.string().email('Invalid email format').required('Email is required'),
  password: yup.string().required('Password is required'),
});

const AuthPage = ({ onAuthSuccess }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState(''); 
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register: registerSignUp,
    handleSubmit: handleSubmitSignUp,
    formState: { errors: errorsSignUp },
    reset: resetSignUp,
  } = useForm({
    resolver: yupResolver(signUpSchema),
  });

  const {
    register: registerSignIn,
    handleSubmit: handleSubmitSignIn,
    formState: { errors: errorsSignIn },
    reset: resetSignIn,
  } = useForm({
    resolver: yupResolver(signInSchema),
  });

  // Helper function to extract a string from error response
  const getErrorMessage = (errorData) => {
    if (!errorData) return 'An error occurred';
    if (typeof errorData === 'string') return errorData;
    if (Array.isArray(errorData)) {
      return errorData.map(e => e.msg || e.message || 'Unknown error').join(', ');
    }
    if (typeof errorData === 'object') {
      return errorData.msg || errorData.message || JSON.stringify(errorData);
    }
    return 'Unknown error';
  };

  const onSignUp = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      // Fixed the unused variable by removing the assignment
      await axios.post('/signup', {
        email: data.email,
        password: data.password,
      });
      
      setSuccess('Registration successful! Redirecting to login...');
      setTimeout(() => {
        resetSignUp();
        setIsSignUp(false); // Switch to login page after signup
        setSuccess('');
      }, 2000);
    } catch (err) {
      const errorMessage = getErrorMessage(err.response?.data?.detail);
      setError(errorMessage || 'Registration failed. Please try again.');
      setSuccess('');
    } finally {
      setIsLoading(false);
    }
  };

  const onSignIn = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Use form data format for signin endpoint
      const formData = new FormData();
      formData.append('username', data.email); // API expects username field
      formData.append('password', data.password);
      
      const response = await axios.post('/signin', formData);
      
      if (response.data.access_token) {
        setSuccess('Login successful! Please wait...');
        localStorage.setItem('token', response.data.access_token);
        
        // Get user data
        const userResponse = await axios.get('/user/me', {
          headers: { Authorization: `Bearer ${response.data.access_token}` }
        });
        
        resetSignIn();
        onAuthSuccess(userResponse.data);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Signin error:', err);
      const errorMessage = getErrorMessage(err.response?.data?.detail);
      setError(errorMessage || 'Invalid email or password');
      setSuccess('');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setSuccess('');
    resetSignIn();
    resetSignUp();
  };

  return (
    <div className="auth-container">
      <div className="auth-forms">
        <div className="auth-logo">
          <h1>SynapzeLM</h1>
        </div>
        <h2 className="form-title">{isSignUp ? 'Create Account' : 'Welcome Back'}</h2>
        
        {isSignUp ? (
          <form onSubmit={handleSubmitSignUp(onSignUp)}>
            <div className="input-field">
              <FaEnvelope className="input-icon" />
              <input
                type="email"
                placeholder="Email"
                {...registerSignUp('email')}
                disabled={isLoading}
              />
            </div>
            {errorsSignUp.email && <p className="error-message">{errorsSignUp.email.message}</p>}
            
            <div className="input-field">
              <FaLock className="input-icon" />
              <input
                type="password"
                placeholder="Password"
                {...registerSignUp('password')}
                disabled={isLoading}
              />
            </div>
            {errorsSignUp.password && <p className="error-message">{errorsSignUp.password.message}</p>}
            
            <div className="input-field">
              <FaLock className="input-icon" />
              <input
                type="password"
                placeholder="Confirm Password"
                {...registerSignUp('confirmPassword')}
                disabled={isLoading}
              />
            </div>
            {errorsSignUp.confirmPassword && <p className="error-message">{errorsSignUp.confirmPassword.message}</p>}
            
            <button type="submit" className={`submit-btn ${isLoading ? 'loading' : ''}`} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Sign Up'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmitSignIn(onSignIn)}>
            <div className="input-field">
              <FaEnvelope className="input-icon" />
              <input
                type="email"
                placeholder="Email"
                {...registerSignIn('email')}
                disabled={isLoading}
              />
            </div>
            {errorsSignIn.email && <p className="error-message">{errorsSignIn.email.message}</p>}
            
            <div className="input-field">
              <FaLock className="input-icon" />
              <input
                type="password"
                placeholder="Password"
                {...registerSignIn('password')}
                disabled={isLoading}
              />
            </div>
            {errorsSignIn.password && <p className="error-message">{errorsSignIn.password.message}</p>}
            
            <button type="submit" className={`submit-btn ${isLoading ? 'loading' : ''}`} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Sign In'}
            </button>
          </form>
        )}

        <div className="auth-switch">
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}
          <button onClick={toggleMode} disabled={isLoading}>
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </div>

        {error && <div className="auth-message error">{error}</div>}
        {success && <div className="auth-message success">{success}</div>}
      </div>
    </div>
  );
};

AuthPage.propTypes = {
  onAuthSuccess: PropTypes.func.isRequired,
};

export default AuthPage;
